import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import clsx from "clsx"
import { useMemo, useState } from "react"

import { DEFAULT_ROOT_FONT_SIZE } from "@constants/fonts"

import styles from "./Icon.module.scss"
import { IconProps, IconSize } from "./Icon.types"

export function getContainerSizeFromSize(size: IconSize | null) {
    const containerMap: { [key in IconSize]: string } = {
        8: "var(--size-2_5)",
        10: "var(--size-3)",
        12: "var(--size-3_5)",
        14: "var(--size-4)",
        16: "var(--size-4_5)",
        18: "var(--size-5_5)",
        20: "var(--size-6)",
        24: "var(--size-7)",
    }

    if (!size) {
        return undefined
    } else {
        return containerMap[size]
    }
}

export default function Icon(props: IconProps) {
    const {
        icon,
        iconOnMouseOver,
        size = null,
        className,
        spin = false,
        rotation,
        container = "square",
        containerClassName,
        isOverlapping,
        iconOnActive,
        isActive,
    } = props

    const [isMouseOver, setIsMouseOver] = useState<boolean>(false)

    const fontSize = useMemo(() => (size ? `${size / DEFAULT_ROOT_FONT_SIZE}rem` : undefined), [size]) // Convert the pixel-based size to rem
    const containerSize = useMemo(() => getContainerSizeFromSize(size), [size])

    const currentIcon = useMemo(() => {
        if (isMouseOver && iconOnMouseOver) {
            return iconOnMouseOver
        } else if (isActive && iconOnActive) {
            return iconOnActive
        } else {
            return icon
        }
    }, [isMouseOver, iconOnMouseOver, icon, isActive, iconOnActive])

    return (
        <div
            className={clsx(styles.base, styles[container], containerClassName, {
                [styles.overlapping]: isOverlapping,
            })}
            style={{
                // Repeating the fontSize here is necessary for the mask to work properly
                fontSize,
                ...((container === "square" || container === "fixedWidth") && {
                    ["--container-size" as string]: containerSize,
                }),
            }}
            onMouseOver={iconOnMouseOver ? () => setIsMouseOver(true) : undefined}
            onMouseOut={iconOnMouseOver ? () => setIsMouseOver(false) : undefined}
        >
            <FontAwesomeIcon
                style={{
                    fontSize,
                }}
                icon={currentIcon}
                className={className}
                spin={spin}
                rotation={rotation}
            />
        </div>
    )
}
